import type { StepConfig, StepIds } from '~/components/premarketplace/partner-config';
import type { PartnerConfigStepFlow } from '~/gql/graphql';

import {
  evaluateVisibleWhen,
  type VisibleWhen,
  type VisibleWhenClause,
  type VisibleWhenOperator,
} from './visible-when';

const KNOWN_STEP_IDS: ReadonlySet<StepIds> = new Set([
  'introduction',
  'eligibility-check',
  'location',
  'categories',
  'household',
  'upgrade-profile',
  'financing-interest',
  'bundle-categories',
  'intent',
  'topics',
  'education',
  'purchase-priority',
  'connect-with-vendors',
  'contact-time',
  'recommended-vendors',
  'bill-upload',
  'contact',
]);

const VISIBLE_WHEN_OPERATORS: ReadonlySet<string> = new Set<VisibleWhenOperator>([
  'equals',
  'notEquals',
  'in',
  'notIn',
]);

export const DEFAULT_STEP_FLOW: StepConfig[] = [
  { id: 'introduction', required: true },
  { id: 'location', required: true },
  { id: 'categories', required: true },
  { id: 'intent', required: true },
  { id: 'recommended-vendors', required: true },
  { id: 'contact', required: true },
];

// Validate the raw `visibleWhen` from API config, dropping malformed clauses
// rather than the whole step. Returns undefined when nothing valid remains, so
// the step falls back to its default (required-based) visibility.
function parseVisibleWhen(raw: unknown): VisibleWhen | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const { match, clauses } = raw as { match?: unknown; clauses?: unknown };
  if (!Array.isArray(clauses)) return undefined;

  const validClauses: VisibleWhenClause[] = [];
  for (const clause of clauses) {
    if (!clause || typeof clause !== 'object') continue;
    const { questionId, operator, values } = clause as {
      questionId?: unknown;
      operator?: unknown;
      values?: unknown;
    };
    if (
      typeof questionId !== 'string' ||
      !KNOWN_STEP_IDS.has(questionId as StepIds) ||
      typeof operator !== 'string' ||
      !VISIBLE_WHEN_OPERATORS.has(operator) ||
      !Array.isArray(values) ||
      !values.every((value) => typeof value === 'string')
    ) {
      continue;
    }
    // Reject empty `values` (would make equals/in always false and notEquals/notIn
    // vacuously true), and require exactly one value for the equality operators —
    // drop the malformed clause so the step falls back to required-based visibility.
    const requiresSingleValue = operator === 'equals' || operator === 'notEquals';
    if (values.length === 0 || (requiresSingleValue && values.length !== 1)) continue;

    validClauses.push({
      questionId: questionId as StepIds,
      operator: operator as VisibleWhenOperator,
      values: values as string[],
    });
  }

  if (validClauses.length === 0) return undefined;
  const normalisedMatch = match === 'any' ? 'any' : match === 'all' ? 'all' : undefined;
  return normalisedMatch ? { match: normalisedMatch, clauses: validClauses } : { clauses: validClauses };
}

export function parseStepFlow(raw: PartnerConfigStepFlow[] | null | undefined): StepConfig[] {
  if (!Array.isArray(raw) || raw.length === 0) return DEFAULT_STEP_FLOW;
  const valid = raw.filter(
    (s): s is PartnerConfigStepFlow =>
      typeof s?.id === 'string' && typeof s?.required === 'boolean' && KNOWN_STEP_IDS.has(s.id as StepIds),
  );
  if (valid.length === 0) return DEFAULT_STEP_FLOW;

  const mapped: StepConfig[] = valid.map((s) => {
    // Validate the raw StepVisibilityCondition from config into our narrowed
    // VisibleWhen domain type (known operators / step ids), dropping anything malformed.
    const visibleWhen = parseVisibleWhen(s.visibleWhen);
    const step: StepConfig = { id: s.id as StepIds, required: s.required };
    return visibleWhen ? { ...step, visibleWhen } : step;
  });

  // A flow with nothing that could ever show (no required step, no branch) is
  // treated as misconfigured — fall back to the safe default.
  return mapped.some((s) => s.required || s.visibleWhen) ? mapped : DEFAULT_STEP_FLOW;
}

function intersects(a: Set<string> | undefined, b: ReadonlySet<string> | undefined): boolean {
  if (!a || !b) return false;
  for (const value of a) if (b.has(value)) return true;
  return false;
}

/**
 * Compute the ordered list of steps to show, given the base flow and the buyer's
 * accumulated answers. Single pass, in order, accumulating answers for
 * already-shown prior steps only — so a clause referencing a later or hidden
 * step reads as "not answered", and hidden steps never feed downstream clauses.
 *
 * Visibility per step:
 *   - has `visibleWhen`  → shown iff the condition evaluates true
 *   - no `visibleWhen`   → today's behaviour: shown iff `required` (backwards-compatible)
 *
 * `bundleSlugs` carries the bundle-eligible category slugs when the channel
 * enables bundle selection. `bundle-categories` is gated on the category
 * selection (dynamic list, so not a static visibleWhen): shown when the selection
 * intersects `bundleSlugs`. This applies whether the step is declared in the flow
 * as an optional (`required:false`) step or injected after `categories` for flows
 * that don't declare it. A `required:true` bundle-categories step (e.g. a
 * bundle-only flow with no `categories` step) is always shown, as normal.
 */
export function computeVisibleSteps(
  steps: StepConfig[],
  answers: ReadonlyMap<StepIds, Set<string>>,
  bundleSlugs?: ReadonlySet<string>,
): StepConfig[] {
  const result: StepConfig[] = [];
  const priorAnswers = new Map<StepIds, Set<string>>();
  const flowDeclaresBundle = steps.some((step) => step.id === 'bundle-categories');
  const bundleGated = bundleSlugs !== undefined && bundleSlugs.size > 0;

  for (const step of steps) {
    // An optional `bundle-categories` declared in the flow is gated on the category
    // selection (same rule as the injected step), not treated as a generic hidden
    // required:false step.
    const isGatedBundleStep =
      step.id === 'bundle-categories' && !step.visibleWhen && step.required === false && bundleGated;
    const shown = isGatedBundleStep
      ? intersects(answers.get('categories'), bundleSlugs)
      : step.visibleWhen
        ? evaluateVisibleWhen(step.visibleWhen, priorAnswers)
        : step.required === true;
    if (!shown) continue;

    result.push(step);
    const answer = answers.get(step.id);
    if (answer) priorAnswers.set(step.id, answer);

    if (
      step.id === 'categories' &&
      !flowDeclaresBundle &&
      bundleSlugs &&
      bundleSlugs.size > 0 &&
      intersects(answers.get('categories'), bundleSlugs)
    ) {
      result.push({ id: 'bundle-categories', required: true });
      const bundleAnswer = answers.get('bundle-categories');
      if (bundleAnswer) priorAnswers.set('bundle-categories', bundleAnswer);
    }
  }

  return result;
}
